import { useState } from "react";
import { boardNameOf, defaultBoardName, type MeResponse } from "@edu/shared";
import { ApiError, setBoardName } from "../../lib/api.ts";
import { accountErrorText } from "../auth/auth-errors.ts";
import { Dialog } from "./Dialog.tsx";
import { PartialSessionNote } from "./PartialSessionNote.tsx";

/**
 * Подпись над курсором на доске.
 *
 * ⚠️ **Заведено по просьбе преподавателя 23.08.2026** (раздел 4
 * `docs/room-feedback-fixes.md`): ученик видел над его курсором «Приходько
 * Владислав», потому что подпись бралась из имени аккаунта, а имя аккаунта
 * приезжает из профиля провайдера входа, где человек записан ФИО.
 *
 * ⚠️ **Имя аккаунта здесь не меняется.** Это отдельное поле ровно на одно
 * место: строку над курсором. В списке учеников, в приглашении и в письмах
 * человек остаётся тем, кем был, и обещать обратное в этом окне нельзя.
 *
 * ⚠️ **Пустое поле это не пустая подпись.** Очистка возвращает умолчание
 * (`defaultBoardName`), которое стоит тут же подсказкой в поле: человек видит,
 * что получится, ДО того как сохранит. Пустой строки над курсором не бывает
 * вовсе, иначе непонятно, чей это курсор.
 *
 * Окном, а не разделом настроек, по той же причине, что смена пароля и учёт
 * занятий: экрана профиля в продукте нет. Когда он появится, окно переедет
 * туда без изменений: оно не знает ни про сайдбар, ни про адрес.
 */
export function BoardNameDialog(props: {
  me: MeResponse;
  onDone: (me: MeResponse) => void;
  onClose: () => void;
}) {
  /**
   * ⚠️ Неполная сессия (9.6 `docs/account-adopt-design.md`): подпись видна во
   * всех комнатах, в том числе у преподавателей, чьих ссылок в этом браузере не
   * открывали, и сервер ответит `session_partial`. Вместо формы объяснение,
   * запроса нет. Отдельным компонентом, а не веткой здесь: у формы свои хуки.
   */
  if (props.me.sessionPartial === true) {
    return <PartialSessionNote title="Подпись на доске" onClose={props.onClose} />;
  }
  return <BoardNameForm {...props} />;
}

function BoardNameForm(props: {
  me: MeResponse;
  onDone: (me: MeResponse) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(props.me.boardName ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fallback = defaultBoardName(props.me.displayName);
  // Что увидит другая сторона, если сохранить прямо сейчас. Считается той же
  // функцией, что и подпись в комнате урока, поэтому разойтись они не могут
  const preview = boardNameOf({ displayName: props.me.displayName, boardName: value });

  const submit = (): void => {
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        // Ответ заменяет сессию целиком: сервер присылает `MeResponse`, чтобы
        // кабинет не собирал её из двух источников
        props.onDone(await setBoardName(value.trim()));
        props.onClose();
      } catch (e) {
        // Вкладка с устаревшим признаком полноты: сервер уже знает, что
        // сессия неполная, и словами это сказать можно
        const known =
          e instanceof ApiError && e.code === "session_partial" ? accountErrorText(e.code) : null;
        setErr(known ?? (e instanceof Error ? e.message : String(e)));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Dialog title="Подпись на доске" note="её видит вторая сторона урока" onClose={props.onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {err !== null && <p className="err">{err}</p>}

        <label className="field">
          <span className="lbl">Как вас подписывать</span>
          <input
            className="fld"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={fallback}
            maxLength={40}
            autoFocus
          />
        </label>
        <p className="hint">
          Над вашим курсором будет: <strong>{preview}</strong>. Одного слова достаточно: длинная
          подпись закрывает собой рисунок, на который вы показываете. Пустое поле возвращает
          подпись по умолчанию. Имя в списке учеников и в приглашениях от этого не меняется.
        </p>

        <div className="form__foot">
          <button type="button" className="btn btn--quiet" onClick={props.onClose} disabled={busy}>
            Отмена
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            Сохранить
          </button>
        </div>
      </form>
    </Dialog>
  );
}
